import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { startSubscriptionCheckout } from '@/utils/payments/subscribe';
import { useMemberApp } from '../AppProvider';
import { IconClose } from './Icons';

const CLINIC = [
  '$400 to $800 per oncology visit in the US',
  '20 minutes, if you can get one',
  'Days or weeks between questions',
  'Hard to ask the small things',
  'Office hours only',
];

const VANI = [
  'Unlimited oncology answers, 24/7',
  'The questions that do not fit in clinic',
  'Private: your memory, your control',
  'Always here for the family too',
  'Voice conversations, PRO only',
  'Priced for US families, open to anyone',
];

const PLANS = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: '$19.99',
    period: '/mo',
    compare: '$39.99',
    note: 'Cancel anytime',
    cta: 'Continue · $19.99',
  },
  {
    id: 'yearly',
    name: 'Yearly',
    price: '$14.99',
    period: '/mo',
    compare: '$19.99',
    note: 'Billed $179.88 once a year · save 25%',
    sash: 'Save 25%',
    cta: 'Continue · $179.88/year',
  },
  {
    id: 'lifetime',
    name: 'Lifetime',
    price: '$999',
    period: ' once',
    compare: '$2,399',
    note: 'One payment. No renewals. For families in this for years.',
    sash: 'Best value',
    featured: true,
    cta: 'Get lifetime access · $999',
  },
];

export default function PricingModal() {
  const { upgradeOpen, setUpgradeOpen, refresh, pricingFocus, setPricingFocus, isPro } = useMemberApp();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const selected = PLANS.some((plan) => plan.id === pricingFocus) ? pricingFocus : 'monthly';
  const chosen = PLANS.find((plan) => plan.id === selected) || PLANS[0];

  useEffect(() => {
    if (!upgradeOpen) {
      setBusy(false);
      setError('');
      setDone(null);
    }
  }, [upgradeOpen]);

  useEffect(() => {
    if (!upgradeOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape' && !busy) setUpgradeOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [upgradeOpen, busy, setUpgradeOpen]);

  if (!upgradeOpen || isPro) return null;

  const close = () => {
    if (busy) return;
    setUpgradeOpen(false);
  };

  const pay = async (type) => {
    if (!isAuthenticated) {
      setUpgradeOpen(false);
      navigate(`/authentication?redirect=${encodeURIComponent('/app')}`);
      return;
    }
    if (busy) return;
    setBusy(true);
    setError('');
    await startSubscriptionCheckout({
      planType: type,
      user,
      onSuccess: async ({ pending, entitlement } = {}) => {
        await refresh();
        setBusy(false);
        setDone({ pending: !!pending, lifetime: type === 'lifetime' || entitlement?.lifetime });
      },
      onDismiss: () => setBusy(false),
      onFail: (err) => {
        setBusy(false);
        setError(err.message || 'Payment did not complete. Please try again.');
      },
    });
  };

  return (
    <div
      className="by-modal-back"
      role="dialog"
      aria-modal="true"
      aria-labelledby="by-upgrade-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div className="by-modal by-modal-plans">
        <button className="by-icon-btn" style={{ position: 'absolute', top: 16, right: 16 }} onClick={close} aria-label="Close">
          <IconClose />
        </button>

        {done ? (
          <div className="by-checkout-ok">
            <span className="by-checkout-mark" aria-hidden="true">✓</span>
            <h2 id="by-upgrade-title">{done.pending ? 'Payment received' : 'You’re in'}</h2>
            <p className="by-lede">
              {done.pending
                ? 'Access is turning on now. If Vani still asks to upgrade, refresh this page in a few seconds.'
                : done.lifetime
                  ? 'Lifetime access is on. Vani is yours, no renewals.'
                  : 'ByOnco PRO is on. Ask Vani anything that did not fit in the clinic visit.'}
            </p>
            <button
              type="button"
              className="by-btn by-btn-dark"
              style={{ width: '100%' }}
              onClick={() => {
                setUpgradeOpen(false);
                navigate('/app');
              }}
            >
              Continue to Vani
            </button>
            {done.pending ? (
              <button
                type="button"
                className="by-btn by-btn-ghost"
                style={{ width: '100%', marginTop: 8 }}
                onClick={() => refresh()}
              >
                Refresh status
              </button>
            ) : null}
          </div>
        ) : (
          <>
            <h2 id="by-upgrade-title">A private oncology companion, when you need her</h2>
            <p className="by-lede" style={{ marginTop: -8 }}>
              Pick a plan. Pay once with a card. Access turns on as soon as the payment confirms.
            </p>
            <div className="by-compare">
              <div className="by-col">
                <h3>Typical US clinic visit</h3>
                {CLINIC.map((line) => (
                  <div className="by-rowline" key={line}><span className="by-x">✕</span>{line}</div>
                ))}
              </div>
              <div className="by-col">
                <span className="by-sash">Save $380+/mo</span>
                <h3>ByOnco</h3>
                {VANI.map((line) => (
                  <div className="by-rowline" key={line}><span className="by-y">✓</span>{line}</div>
                ))}
              </div>
            </div>

            <div className="by-plans" role="radiogroup" aria-label="Choose a plan">
              {PLANS.map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  className={`by-plan${selected === plan.id ? ' is-on' : ''}${plan.featured ? ' is-life' : ''}`}
                  onClick={() => setPricingFocus(plan.id)}
                  aria-checked={selected === plan.id}
                  role="radio"
                >
                  {plan.sash ? <span className="by-plan-sash">{plan.sash}</span> : null}
                  <b>{plan.name}</b>
                  <div className="by-plan-price">
                    <s>{plan.compare}</s>
                    <strong>{plan.price}</strong>
                    <small>{plan.period}</small>
                  </div>
                  <p>{plan.note}</p>
                </button>
              ))}
            </div>

            {error ? <p className="by-checkout-err" role="alert">{error}</p> : null}
            <button className="by-btn by-btn-dark" style={{ width: '100%' }} disabled={busy} onClick={() => pay(selected)}>
              {busy ? 'Opening secure checkout…' : chosen.cta}
            </button>
            <div className="by-trust">
              <span>Card via Razorpay</span>
              <span>Access on confirmation</span>
              <span>Cancel monthly anytime</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
